import { documentsMinReady } from "../complianceLabels";
import type { ComplianceCaseDetail, ComplianceCaseSummary } from "../types";

export type ReadinessItemKey = "documents" | "validation" | "passport" | "share";

export type ReadinessItem = {
  key: ReadinessItemKey;
  done: boolean;
};

export type CaseReadiness = {
  items: ReadinessItem[];
  completed: number;
  total: number;
  /** 0–100 */
  percent: number;
};

/** Shared readiness rules for case detail checklist (US-301). */
export function computeCaseReadiness(
  caseData: Pick<
    ComplianceCaseDetail,
    "documents" | "latest_validation" | "passport" | "share_deliveries" | "status"
  > | null
): CaseReadiness {
  if (!caseData) {
    return {
      items: [
        { key: "documents", done: false },
        { key: "validation", done: false },
        { key: "passport", done: false },
        { key: "share", done: false },
      ],
      completed: 0,
      total: 4,
      percent: 0,
    };
  }

  const items: ReadinessItem[] = [
    { key: "documents", done: documentsMinReady(caseData.documents || []) },
    { key: "validation", done: caseData.latest_validation?.passed === true },
    { key: "passport", done: Boolean(caseData.passport) },
    {
      key: "share",
      done:
        (caseData.share_deliveries?.length ?? 0) > 0 || caseData.status === "shared",
    },
  ];
  const completed = items.filter((i) => i.done).length;
  const total = items.length;
  return {
    items,
    completed,
    total,
    percent: Math.round((completed / total) * 100),
  };
}

/** Coarse list-row progress from summary status alone. */
export function readinessPercentFromStatus(status: ComplianceCaseSummary["status"]): number {
  switch (status) {
    case "draft":
      return 0;
    case "collecting_documents":
      return 25;
    case "issues_found":
    case "resolving":
      return 40;
    case "validated":
      return 50;
    case "passport_ready":
      return 75;
    case "shared":
      return 100;
    default:
      return 0;
  }
}
